import * as THREE from "three";
import { getSceneActions, getSceneState } from "../../stores/sceneStore";
import {
  getShallowAttachmentSitesForLibraryTargets,
  isLibraryGripperDrag,
} from "../../utils/libraryGripperAttachTargets";
import { findSiteObjectInViewerScene } from "../scene/sceneObjectLookups";
import {
  LIBRARY_GRIPPER_ATTACH_HALO_RADIUS,
  LIBRARY_GRIPPER_ATTACH_MARKER_COLOR,
  LIBRARY_GRIPPER_ATTACH_MARKER_HOVER_COLOR,
  LIBRARY_GRIPPER_ATTACH_MARKER_POOL,
} from "../scene/sceneConstants";
import { isPointerOverLibraryPanel } from "../scene/sceneLibraryDropHelpers";

export function clearLibraryDropShadow(libraryDropShadowRef) {
  const shadow = libraryDropShadowRef.current;
  if (shadow) shadow.visible = false;
}

export function ensureLibraryDropShadow({ sceneRef, libraryDropShadowRef }) {
  if (!sceneRef.current) return null;
  if (
    libraryDropShadowRef.current &&
    sceneRef.current.getObjectById(libraryDropShadowRef.current.id)
  ) {
    return libraryDropShadowRef.current;
  }
  const markerGroup = new THREE.Group();
  markerGroup.name = "libraryDropShadow";

  const fillGeometry = new THREE.RingGeometry(0.05, 0.12, 48);
  fillGeometry.rotateX(-Math.PI / 2);
  const fillMaterial = new THREE.MeshBasicMaterial({
    color: 0x06b6d4,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
  });
  const fill = new THREE.Mesh(fillGeometry, fillMaterial);
  fill.renderOrder = 9999;
  markerGroup.add(fill);

  const ringGeometry = new THREE.RingGeometry(0.155, 0.23, 48);
  ringGeometry.rotateX(-Math.PI / 2);
  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0x22d3ee,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeometry, ringMaterial);
  ring.renderOrder = 10000;
  markerGroup.add(ring);

  markerGroup.visible = false;
  sceneRef.current.add(markerGroup);
  libraryDropShadowRef.current = markerGroup;
  return markerGroup;
}

export function clearLibraryAttachMarkers({
  libraryAttachMarkersRef,
  libraryGripperAttachHoverSiteRef,
}) {
  libraryGripperAttachHoverSiteRef.current = null;
  const group = libraryAttachMarkersRef.current;
  if (!group) return;
  group.visible = false;
  for (let i = 0; i < group.children.length; i += 1) {
    group.children[i].visible = false;
  }
}

export function ensureLibraryAttachMarkersGroup({ sceneRef, libraryAttachMarkersRef }) {
  if (!sceneRef.current) return null;
  let group = libraryAttachMarkersRef.current;
  if (group && sceneRef.current.getObjectById(group.id)) {
    return group;
  }
  group = new THREE.Group();
  group.name = "libraryGripperAttachTargets";
  for (let i = 0; i < LIBRARY_GRIPPER_ATTACH_MARKER_POOL; i += 1) {
    const markerRoot = new THREE.Group();
    const haloGeom = new THREE.SphereGeometry(LIBRARY_GRIPPER_ATTACH_HALO_RADIUS, 32, 24);
    const haloMat = new THREE.MeshBasicMaterial({
      color: LIBRARY_GRIPPER_ATTACH_MARKER_COLOR,
      transparent: true,
      opacity: 0.12,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
    });
    const halo = new THREE.Mesh(haloGeom, haloMat);
    halo.renderOrder = 10000;
    halo.name = "attachHaloSphere";
    markerRoot.add(halo);

    const torusGeom = new THREE.TorusGeometry(0.072, 0.009, 12, 48);
    const torusMat = new THREE.MeshBasicMaterial({
      color: LIBRARY_GRIPPER_ATTACH_MARKER_COLOR,
      transparent: true,
      opacity: 0.82,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
    });
    const torus = new THREE.Mesh(torusGeom, torusMat);
    torus.renderOrder = 10001;
    torus.name = "attachTorusRing";
    markerRoot.add(torus);
    markerRoot.visible = false;
    group.add(markerRoot);
  }
  sceneRef.current.add(group);
  libraryAttachMarkersRef.current = group;
  return group;
}

export function updateGripperAttachTargetsPreview({
  cameraRef,
  clearAttachMarkers,
  ensureAttachMarkersGroup,
  libraryDragDataRef,
  libraryDragPointerRef,
  libraryGripperAttachHoverSiteRef,
  rendererRef,
  sceneRef,
}) {
  const dragData = libraryDragDataRef.current;
  if (!isLibraryGripperDrag(dragData)) {
    clearAttachMarkers();
    return;
  }
  const canvas = rendererRef.current?.domElement;
  const rect = canvas?.getBoundingClientRect?.();
  const pointer = libraryDragPointerRef.current;
  const scene = sceneRef.current;
  if (!canvas || !rect || !cameraRef.current || !scene) {
    clearAttachMarkers();
    return;
  }
  if (isPointerOverLibraryPanel(pointer)) {
    clearAttachMarkers();
    return;
  }

  let allSites;
  try {
    allSites = getSceneActions().findAttachmentSites("attachment_site") || [];
  } catch {
    allSites = [];
  }
  const { bodies } = getSceneState();
  const targets = getShallowAttachmentSitesForLibraryTargets(bodies, allSites).slice(
    0,
    LIBRARY_GRIPPER_ATTACH_MARKER_POOL
  );
  if (targets.length === 0) {
    clearAttachMarkers();
    return;
  }

  const group = ensureAttachMarkersGroup();
  if (!group) return;

  const camera = cameraRef.current;
  const margin = 8;
  const clientX = pointer
    ? THREE.MathUtils.clamp(pointer.x, rect.left + margin, rect.right - margin)
    : rect.left + rect.width * 0.5;
  const clientY = pointer
    ? THREE.MathUtils.clamp(pointer.y, rect.top + margin, rect.bottom - margin)
    : rect.top + rect.height * 0.62;

  const pickRadiusPx = 104;
  const screenSamples = [];
  const worldPos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3();

  for (let i = 0; i < targets.length; i += 1) {
    const site = targets[i];
    const siteObj = findSiteObjectInViewerScene(scene, site.siteUuid);
    const markerRoot = group.children[i];
    if (!markerRoot) continue;
    if (!siteObj) {
      markerRoot.visible = false;
      continue;
    }
    siteObj.updateMatrixWorld(true);
    siteObj.matrixWorld.decompose(worldPos, quat, scale);
    markerRoot.position.copy(worldPos);
    markerRoot.quaternion.copy(quat);
    const projected = worldPos.clone().project(camera);
    const sx = (projected.x * 0.5 + 0.5) * rect.width + rect.left;
    const sy = (-projected.y * 0.5 + 0.5) * rect.height + rect.top;
    const dist = Math.hypot(clientX - sx, clientY - sy);
    screenSamples.push({ site, dist, markerRoot });
  }

  if (screenSamples.length === 0) {
    clearAttachMarkers();
    return;
  }

  let bestSiteUuid = null;
  let bestDist = Infinity;
  for (const row of screenSamples) {
    if (row.dist <= pickRadiusPx && row.dist < bestDist) {
      bestDist = row.dist;
      bestSiteUuid = row.site.siteUuid;
    }
  }
  libraryGripperAttachHoverSiteRef.current = bestSiteUuid;

  for (const row of screenSamples) {
    const hover = Boolean(bestSiteUuid && row.site.siteUuid === bestSiteUuid);
    const halo = row.markerRoot.getObjectByName("attachHaloSphere");
    const haloMat = halo?.material;
    if (haloMat) {
      haloMat.color.setHex(
        hover ? LIBRARY_GRIPPER_ATTACH_MARKER_HOVER_COLOR : LIBRARY_GRIPPER_ATTACH_MARKER_COLOR
      );
      haloMat.opacity = hover ? 0.22 : row.dist <= pickRadiusPx + 36 ? 0.16 : 0.11;
    }
    const torus = row.markerRoot.getObjectByName("attachTorusRing");
    const torusMat = torus?.material;
    if (torusMat) {
      torusMat.color.setHex(
        hover ? LIBRARY_GRIPPER_ATTACH_MARKER_HOVER_COLOR : LIBRARY_GRIPPER_ATTACH_MARKER_COLOR
      );
      torusMat.opacity = hover ? 0.96 : row.dist <= pickRadiusPx + 32 ? 0.88 : 0.62;
    }
    row.markerRoot.visible = true;
  }
  for (let j = screenSamples.length; j < group.children.length; j += 1) {
    group.children[j].visible = false;
  }
  group.visible = true;
}

export function disposeObjectTree(ref) {
  const object = ref.current;
  if (!object) return;
  if (object.parent) object.parent.remove(object);
  object.traverse((child) => {
    child.geometry?.dispose?.();
    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material?.dispose?.());
    } else {
      child.material?.dispose?.();
    }
  });
  ref.current = null;
}
